import { X, Search } from "lucide-react"
import React, { useRef } from "react"
import { cn } from "@/utils/styles.ts"

interface SearchBarGeneralProps {
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  className?: string
  onClear?: () => void
  disabled?: boolean
}

const SearchBarGeneral: React.FC<SearchBarGeneralProps> = ({
  value,
  onChange,
  placeholder,
  className,
  onClear,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else {
      onChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>)
    }
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && value) {
      e.preventDefault()
      handleClear()
    }
  }

  return (
    <div className={cn("relative max-w-md lg:max-w-lg", className)}>
      <div className="relative flex items-center">
        <Search
          className="pointer-events-none absolute left-3 h-5 w-5 text-gray-400"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          className={cn(
            "h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 text-base text-gray-900 placeholder-gray-500 transition-all",
            value ? "pr-10" : "pr-4",
            "focus:border-brown-500 focus:outline-none focus:ring-2 focus:ring-brown-500 focus:ring-offset-2",
            "[@media(hover:hover)]:hover:border-gray-400",
            "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-60",
          )}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          disabled={disabled}
          aria-label={placeholder}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-brown-500 focus:ring-offset-2 [@media(hover:hover)]:hover:bg-gray-100 [@media(hover:hover)]:hover:text-gray-600"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default SearchBarGeneral
